import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

import useIsMinBreakpoint from '@hero/react-hooks/mediaQuery/useIsMinBreakpoint';
import InfoIcon from '@hero/ui-kit/icons/utility/InfoIcon';
import Button from '@hero/ui-kit/inputs/Button';
import Form, { useFormContext } from '@hero/ui-kit/inputs/Form';
import ImageUpload from '@hero/ui-kit/inputs/ImageUpload';
import Input from '@hero/ui-kit/inputs/Input';
import Option from '@hero/ui-kit/inputs/Option';
import Select from '@hero/ui-kit/inputs/Select';
import TextArea from '@hero/ui-kit/inputs/TextArea';
import Container from '@hero/ui-kit/layout/Container';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import { UserDetailsProps } from '.';
import ButtonToggler from '../../../../components/ButtonToggler';
import { useUserStatusContext } from '../../../../context/UserStatus';
import useGetCareManagers from '../../../FleetManagement/api/useGetCareManagers';
import useUnassignCareManagers from '../../../FleetManagement/api/useUnassignCareManagers';
import { IAMListRole } from '../../../IAM/types';
import { USERS_LIST_KEY } from '../../api/constants';
import useCreateExternalUser from '../../api/useCreateExternalUser';
import useDeactivateCareManager from '../../api/useDeactivateCareManager';
import useRemoveUserInvite from '../../api/useRemoveUserInvite';
import useUpdateExternalUser from '../../api/useUpdateExternalUser';
import useUpdateUserInvite from '../../api/useUpdateUserInvite';
import { CreateExternalUserParams, UpdateExternalUserParams, UpdateUserAction } from '../../types';
import {
    buildExternalUserPayload,
    buildUserFormDefaultValues,
    getTupleValue,
    validationSchema
} from '../../utils';
import * as Style from './style.module.scss';
import UserErrorComponent from './UserErrorComponent';

type UserStatus = 'ACTIVE' | 'INVITED' | 'INVITE_EXPIRED' | 'DEACTIVATED';

interface FormFieldsProps {
    isPending: boolean;
    roles?: IAMListRole[];
    status: UserStatus;
    isLoggedUser?: boolean;
    resetMFA: boolean;
    setResetMFA: (checked: boolean) => void;
    onDeactivate?: () => void;
    currentRoleId?: string;
}

const getSubmitLabel = (status: UserStatus, isPending: boolean): string => {
    if (isPending) {
        return status === 'ACTIVE' ? 'Updating...' : 'Pending...';
    }

    if (status === 'ACTIVE') return 'Update';
    if (status === 'DEACTIVATED') return 'Reactivate User';
    return 'Send invite';
};

const FormFields: React.FC<FormFieldsProps> = ({
    isPending,
    roles,
    status,
    isLoggedUser,
    resetMFA,
    setResetMFA,
    onDeactivate,
    currentRoleId
}) => {
    const { formState, setValue } = useFormContext();
    const isMinL = useIsMinBreakpoint('l');
    const { permissions } = useUserStatusContext();
    const hasPermission = permissions.iam.inviteUserEdit;

    const isSubmitDisabled = React.useMemo(() => {
        if (status === 'ACTIVE') {
            return isPending || !formState.isValid || !formState.isDirty || isLoggedUser;
        }
        return isPending || !formState.isValid || !hasPermission;
    }, [status, isPending, formState.isValid, formState.isDirty, isLoggedUser, hasPermission]);

    const showMFA = status === 'ACTIVE' || status === 'DEACTIVATED';

    return (
        <Section noDefaultPadding paddingHorizontal="none" paddingVertical="none">
            <Container gridTemplateColumns={isMinL ? '1fr 1fr' : '1fr'} gridColumnGap="regular">
                <Input name="first_name" displayName="First name" disabled={isLoggedUser} />
                <Input name="last_name" displayName="Last name" disabled={isLoggedUser} />
            </Container>

            <Container gridTemplateColumns={'1fr'}>
                <Input name="email" displayName="Email" type="email" disabled={isLoggedUser} />
                <Select
                    name="role"
                    displayName="Role"
                    disabled={isLoggedUser}
                    defaultValue={currentRoleId}
                    onChange={(value) =>
                        setValue('role', value, { shouldDirty: true, shouldValidate: true })
                    }
                >
                    {(roles ?? []).map((role) => (
                        <Option key={role.id} value={role.id} labelText={role.name} />
                    ))}
                </Select>
            </Container>

            <Container gridTemplateColumns={isMinL ? '1fr 1fr' : '1fr'} gridColumnGap="regular">
                <Input name="phone" displayName="Phone" type="tel" disabled={isLoggedUser} />
                <Input name="specialty" displayName="Specialty" disabled={isLoggedUser} />
            </Container>

            <Container gridTemplateColumns={isMinL ? '1fr 2fr' : '1fr'} gridColumnGap="regular">
                <ImageUpload name="image" displayName="Profile image" disabled={isLoggedUser} />
                <TextArea name="bio" displayName="Bio" rows={3} disabled={isLoggedUser} />
            </Container>

            {showMFA ? (
                <>
                    <hr className={Style.divider} />
                    <div className={Style.mfaResetWrapper}>
                        <div>
                            <P strong noDefaultMargin>
                                MFA Reset
                            </P>
                            <P noDefaultMargin>Authenticator app</P>
                        </div>
                        <ButtonToggler
                            disabled={status === 'ACTIVE' && isLoggedUser}
                            checked={resetMFA}
                            onChange={(checked) => setResetMFA(checked)}
                        />
                    </div>
                    {resetMFA ? (
                        <div className={Style.infoResetMFA}>
                            <InfoIcon width={20} height={22} type="utility" />
                            <P noDefaultMargin>
                                The user will be required to set up MFA again on next login.
                            </P>
                        </div>
                    ) : null}
                </>
            ) : null}

            {status === 'ACTIVE' ? (
                <div className={Style.btnWrapper}>
                    <Button
                        variant="outlineSecondary"
                        disabled={isLoggedUser}
                        onClick={onDeactivate}
                    >
                        Deactivate User
                    </Button>
                    <Button
                        type="submit"
                        disabled={(!hasPermission || isSubmitDisabled) && !resetMFA}
                    >
                        {getSubmitLabel(status, isPending)}
                    </Button>
                </div>
            ) : (
                <Section noDefaultPadding paddingTop="small" centered>
                    <Button type="submit" disabled={isSubmitDisabled} width="large">
                        {getSubmitLabel(status, isPending)}
                    </Button>
                </Section>
            )}
        </Section>
    );
};

const EditUser: React.FC<UserDetailsProps> = ({
    user,
    userName,
    closeModal,
    roles,
    isLoggedUser
}) => {
    const status = user.invite_status as UserStatus;
    const userId = user.invite_id;
    const userEmail = user.email;
    const roleId = getTupleValue(user.role, 'id') as string | undefined;

    const isCareManager = getTupleValue(user.role, 'name') === 'Hero Care Manager Role';

    const { data: careManagers } = useGetCareManagers();
    const { mutateAsync: unassignCareManagers } = useUnassignCareManagers();

    const careManagerId = React.useMemo(() => {
        if (!isCareManager || !user.hero_user_id || !careManagers) {
            return null;
        }

        return careManagers.find((cm) => cm.hero_user_id === user.hero_user_id)?.id ?? null;
    }, [isCareManager, user.hero_user_id, careManagers]);

    const queryClient = useQueryClient();

    const [showDeactivateConfirm, setShowDeactivateConfirm] = React.useState(false);
    const [pendingDowngradeValues, setPendingDowngradeValues] = React.useState<Record<
        string,
        string
    > | null>(null);
    const [resetMFA, setResetMFA] = React.useState(false);
    const [proceed, setProceed] = React.useState(status === 'ACTIVE');

    const {
        mutate: updateInviteMutate,
        mutateAsync: updateInvite,
        error: updateError,
        isPending: isUpdatePending,
        isSuccess: isRevokeSuccess
    } = useUpdateUserInvite();

    const {
        mutateAsync: updateExternalUser,
        isPending: isExternalUpdatePending,
        error: externalUpdateError
    } = useUpdateExternalUser();

    const {
        mutateAsync: createExternalUser,
        isPending: isExternalCreatePending,
        error: externalCreateError
    } = useCreateExternalUser();

    const {
        mutateAsync: deactivateUser,
        isPending: isDeactivateInvitePending,
        error: deactivateInviteError
    } = useRemoveUserInvite();

    const {
        mutateAsync: deactivateCareManager,
        isPending: isDeactivateCMPending,
        error: deactivateCMError
    } = useDeactivateCareManager();

    const isDeactivatePending = isDeactivateInvitePending || isDeactivateCMPending;
    const deactivateError = deactivateInviteError || deactivateCMError;

    const { permissions } = useUserStatusContext();
    const isPending = isUpdatePending || isExternalUpdatePending || isExternalCreatePending;

    const defaultValues = React.useMemo(() => buildUserFormDefaultValues(user), [user]);

    const executeSubmit = React.useCallback(
        (values: Record<string, string>, shouldUnassign: boolean) => {
            const promises: Promise<unknown>[] = [];
            const isRoleChanged = values.role !== roleId;
            const roleName = roles?.find((r) => r.id === values.role)?.name;

            const payload = buildExternalUserPayload(values, {
                externalUserId: user.external_user_id,
                heroUserId: user.hero_user_id,
                originalImageUrl: user.image_url,
                roleName
            });

            promises.push(
                user.external_user_id
                    ? updateExternalUser(payload as UpdateExternalUserParams)
                    : createExternalUser(payload as CreateExternalUserParams)
            );

            const actions: UpdateUserAction[] = [];

            if (status === 'INVITED') {
                actions.push('RESEND_INVITE');
            }
            if (status === 'DEACTIVATED') {
                actions.push('ENABLE_INVITE');
            }
            if (isRoleChanged) {
                actions.push('UPDATE_ROLE');
            }
            if (resetMFA) {
                actions.push('RESET_MFA');
            }

            if (actions.length > 0 && userId) {
                promises.push(
                    updateInvite({
                        id: userId,
                        action: actions,
                        ...(isRoleChanged && { role_id: values.role })
                    })
                );
            }

            Promise.all(promises)
                .then(() => {
                    queryClient.invalidateQueries({ queryKey: [USERS_LIST_KEY] });

                    return shouldUnassign && careManagerId !== null
                        ? unassignCareManagers({
                              payload: { care_manager_ids: [careManagerId], member_ids: [] }
                          })
                        : undefined;
                })
                .then(() => closeModal())
                .catch(() => {});
        },
        [
            user.external_user_id,
            user.hero_user_id,
            user.image_url,
            updateExternalUser,
            createExternalUser,
            updateInvite,
            resetMFA,
            roleId,
            roles,
            careManagerId,
            unassignCareManagers,
            userId,
            status,
            closeModal,
            queryClient
        ]
    );

    const handleSubmit = React.useCallback(
        (values: Record<string, string>) => {
            const isRoleChanged = values.role !== roleId;
            const newRoleName = roles?.find((r) => r.id === values.role)?.name;
            const shouldUnassign =
                isCareManager && isRoleChanged && newRoleName === 'Hero Read Only Role';

            if (shouldUnassign) {
                setPendingDowngradeValues(values);
                return;
            }

            executeSubmit(values, false);
        },
        [roleId, roles, isCareManager, executeSubmit]
    );

    // Close modal after successful revoke (INVITED only)
    React.useEffect(() => {
        if (isRevokeSuccess && status === 'INVITED') {
            closeModal();
        }
    }, [isRevokeSuccess, status, closeModal]);

    const error = updateError || externalUpdateError || externalCreateError;

    if (!!error || !!deactivateError) {
        return (
            <UserErrorComponent
                updateError={error as { errors: string[] } | null}
                deactivateError={deactivateError as { errors: string[] } | null}
                userEmail={userEmail}
                userName={userName}
                closeModal={closeModal}
            />
        );
    }

    // ACTIVE: role downgrade confirmation screen
    if (pendingDowngradeValues !== null) {
        return (
            <Section centered>
                <P size="large" strong>
                    Are you sure you want to change this role?
                </P>
                <P noDefaultMargin className={Style.careManagerWarning}>
                    All Members assigned to this Care Manager will be unassigned.
                </P>
                <div className={Style.btnWrapper}>
                    <Button
                        disabled={isPending}
                        onClick={() => setPendingDowngradeValues(null)}
                        variant="outlineSecondary"
                    >
                        No, keep role
                    </Button>
                    <Button
                        disabled={isPending}
                        onClick={() => {
                            executeSubmit(pendingDowngradeValues, true);
                            setPendingDowngradeValues(null);
                        }}
                    >
                        {isPending ? 'Pending...' : 'Yes, change role'}
                    </Button>
                </div>
            </Section>
        );
    }

    // ACTIVE: deactivate confirmation screen
    if (showDeactivateConfirm) {
        return (
            <Section centered>
                <P size="large" strong>
                    Are you sure you want to deactivate account?
                </P>
                <P noDefaultMargin>
                    This user will lose their access immediately. Do you want to proceed?
                </P>
                {isCareManager ? (
                    <P noDefaultMargin className={Style.careManagerWarning}>
                        All Members assigned to this Care Manager will be unassigned.
                    </P>
                ) : null}
                <div className={Style.btnWrapper}>
                    <Button
                        disabled={isDeactivatePending}
                        onClick={() => setShowDeactivateConfirm(false)}
                        variant="outlineSecondary"
                    >
                        No, keep active
                    </Button>
                    <Button
                        disabled={isDeactivatePending}
                        onClick={() => {
                            // A provisioned user (has an rtm/commerce identity) is deactivated
                            // via rtm, which org-scopes the IAM deactivation (invite OR implicit
                            // grant) and always unassigns their members — for ANY role, not just
                            // care managers. A pending invite with no external_user_id has no
                            // commerce identity, so it just gets revoked in IAM directly.
                            if (user.external_user_id) {
                                deactivateCareManager({
                                    external_user_id: user.external_user_id
                                })
                                    .then(() => {
                                        setShowDeactivateConfirm(false);
                                        closeModal();
                                    })
                                    .catch(() => {});
                            } else {
                                deactivateUser({ id: userId! })
                                    .then(() => {
                                        setShowDeactivateConfirm(false);
                                        closeModal();
                                    })
                                    .catch(() => {});
                            }
                        }}
                    >
                        {isDeactivatePending ? 'Pending...' : 'Yes, deactivate'}
                    </Button>
                </div>
            </Section>
        );
    }

    // INVITED: pre-screen for pending/expired invite
    if (!proceed && (status === 'INVITED' || status === 'INVITE_EXPIRED')) {
        const isInviteExpired = status === 'INVITE_EXPIRED' || user.expired;
        const isUpdateDisabled = !permissions.iam.inviteUserEdit || isUpdatePending;

        return (
            <Section centered noDefaultPadding>
                <P
                    noDefaultMargin
                    strong
                    styles={{ fontSize: '24px', lineHeight: '24px', marginBottom: '3.6rem' }}
                >
                    {`Invite ${isInviteExpired ? 'expired' : 'pending'}`}
                </P>
                {isInviteExpired ? (
                    <P
                        noDefaultMargin
                        styles={{
                            textAlign: 'center',
                            paddingLeft: '3.6rem',
                            paddingRight: '3.6rem'
                        }}
                    >
                        The invite for <strong>{userEmail}</strong> has expired. Invites must be
                        accepted within 10 days of being sent.
                    </P>
                ) : (
                    <P
                        noDefaultMargin
                        styles={{
                            textAlign: 'center',
                            paddingLeft: '3.6rem',
                            paddingRight: '3.6rem'
                        }}
                    >
                        The invite for <strong>{userEmail}</strong> has not been accepted yet. What
                        would you like to do?
                    </P>
                )}

                <div className={Style.btnWrapper}>
                    {!isInviteExpired ? (
                        <Button
                            disabled={isUpdateDisabled}
                            variant="outlineSecondary"
                            onClick={() =>
                                updateInviteMutate({ id: userId!, action: ['REVOKE_INVITE'] })
                            }
                        >
                            {isUpdatePending ? 'Pending...' : 'Mark as expired'}
                        </Button>
                    ) : null}

                    <Button
                        disabled={isUpdateDisabled}
                        onClick={() =>
                            updateInviteMutate({ id: userId!, action: ['RESEND_INVITE'] })
                        }
                    >
                        {isUpdatePending ? 'Pending...' : 'Resend Invite'}
                    </Button>
                </div>
            </Section>
        );
    }

    // DEACTIVATED: pre-screen for reactivation
    if (!proceed && status === 'DEACTIVATED') {
        return (
            <Section centered noDefaultPadding>
                <P
                    noDefaultMargin
                    strong
                    styles={{ fontSize: '24px', lineHeight: '24px', marginBottom: '1.2rem' }}
                >
                    {userName} (Deactivated)
                </P>
                <P noDefaultMargin styles={{ marginBottom: '3.6rem' }}>
                    {userEmail}
                </P>
                <P
                    noDefaultMargin
                    styles={{ textAlign: 'center', paddingLeft: '3.6rem', paddingRight: '3.6rem' }}
                >
                    The user account for {userEmail} is currently deactivated. Initiate the
                    reactivation process. This will allow the user to access their account again.
                </P>
                <Button
                    onClick={() => setProceed(true)}
                    styles={{ marginTop: '6rem' }}
                    disabled={!permissions.iam.inviteUserEdit}
                >
                    Reactivate account
                </Button>
            </Section>
        );
    }

    // Main form view (shared across all statuses)
    return (
        <>
            <Section centered noDefaultPadding marginBottom="large">
                <H role="h5" noDefaultMargin>
                    {userName}
                </H>
                <P noDefaultMargin>{userEmail}</P>
            </Section>

            {isLoggedUser ? (
                <P size="small">
                    You cannot modify your role, reset your MFA, or delete your account.
                </P>
            ) : null}

            <Form
                validationSchema={validationSchema}
                submitFn={handleSubmit}
                defaultValues={defaultValues}
                validationMode="onTouched"
            >
                <FormFields
                    isPending={isPending}
                    roles={roles}
                    status={status}
                    isLoggedUser={isLoggedUser}
                    resetMFA={resetMFA}
                    setResetMFA={setResetMFA}
                    onDeactivate={() => setShowDeactivateConfirm(true)}
                    currentRoleId={roleId}
                />
            </Form>
        </>
    );
};

export default EditUser;
